import React, { useState, useEffect } from "react";
import { getSwapHistoryDataByAddress, fetchRuneMarketData } from "../utils/api";
import MobileDetect from "mobile-detect";

const TradingTable = ({
  data,
  filters,
  sort = { column: "profit", order: "desc" },
  setSort,
  currentPage,
  setCurrentPage,
  showNotification,
  setPopup,
  totalPages,
  setPartialLoading,
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [trades, setTrades] = useState({});
  const [limit, setLimit] = useState(10);

  const [isMobile, setIsMobile] = useState(false); // State to track if the user is on mobile

  useEffect(() => {
    const md = new MobileDetect(window.navigator.userAgent); 
    setIsMobile(md.mobile() !== null); 
  }, []);

  // Helper Functions
  const toggleExpand = async(address) => {
    setPartialLoading(true);
    if (!expandedRows.has(address)) {
      const tempData = await getSwapHistoryDataByAddress(address, limit, 0);
      const tempTrades = trades;
      tempTrades[address] = {};
      tempTrades[address].data = tempData.data;
      tempTrades[address].total = Math.ceil(tempData.total / limit);
      tempTrades[address].currentPage = 1;
      setTrades(tempTrades);
    }

    setExpandedRows((prev) => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(address)) newExpanded.delete(address);
      else {
        newExpanded.add(address);
      }
      return newExpanded;
    });

    setPartialLoading(false);
  };

  const nextSubPage = async (address) => {
    setPartialLoading(true);
    const tempTrades = trades;
    if (tempTrades[address]?.total > tempTrades[address]?.currentPage) {
      const tempData = await getSwapHistoryDataByAddress(address, limit, tempTrades[address].currentPage * limit);
      tempTrades[address].data = tempData.data;
      tempTrades[address].currentPage++;
      setTrades(tempTrades);
    }
    setPartialLoading(false);
  }

  const prevSubPage = async (address) => {
    setPartialLoading(true);
    const tempTrades = trades;
    if (tempTrades[address]?.currentPage > 1) {
      const tempData = await getSwapHistoryDataByAddress(address, limit, (tempTrades[address].currentPage - 2) * limit);
      tempTrades[address].data = tempData.data;
      tempTrades[address].currentPage--;
      setTrades(tempTrades);
    }
    setPartialLoading(false);
  }

  const copyTrade = async (address) => {
    try {
      await navigator.clipboard.writeText(address);
      const shortAddress = `${address.slice(0, 4)}...${address.slice(-4)}`;
      showNotification(`Started copy trading for ${shortAddress}`);

      // window.location.href =
      window.open(`https://telegram.me/dotswap_trade_bot?start=__${address}`, "_blank");

    } catch (error) {
      console.error("Error in copyTrade:", error);
      showNotification(`Failed to send to Telegram: ${error.message}`);
    }
  };

  const copyAddress = (address) => {
    navigator.clipboard.writeText(address);
    showNotification(
      `Copied address: ${address.slice(0, 4)}...${address.slice(-4)}`
    );
  };

  const copyTicker = (ticker) => {
    navigator.clipboard.writeText(ticker);
    showNotification(`Copied ticker: ${ticker}`);
  };

  const handleRuneInfoClick = async (ticker) => {
    const runeData = await fetchRuneMarketData(ticker);
    if (runeData) {
      setPopup({ type: "runeMarket", data: runeData });
    } else {
      showNotification(`Failed to fetch data for ${ticker}`);
    }
  };

  // Render
  return (
    <div className="flex-1 overflow-x-auto bg-gray-700 p-2 rounded-lg">
      {/* Main Table */}
        <table className="w-full text-left text-xs sm:text-sm p-2 bg-gray-700 rounded-lg">
          <thead>
            <tr className="bg-gray-800 sticky top-0 z-10 whitespace-nowrap">
              <th className="p-1 sm:p-2"></th>
              <th
                className="p-1 sm:p-2 cursor-pointer min-w-[100px]"
                onClick={() =>
                  setSort({
                    column: "address",
                    order:
                      sort.column === "address" && sort.order === "asc"
                        ? "desc"
                        : "asc",
                  })
                }
              >
                Address{" "}
                {sort.column === "address" &&
                  (sort.order === "asc" ? "▲" : "▼")}
              </th>
              <th
                className="p-1 sm:p-2 cursor-pointer"
                onClick={() =>
                  setSort({
                    column: "profit",
                    order:
                      sort.column === "profit" && sort.order === "asc"
                        ? "desc"
                        : "asc",
                  })
                }
              >
                Profit %{" "}
                {sort.column === "profit" &&
                  (sort.order === "asc" ? "▲" : "▼")}
              </th>
              {!isMobile && (
                <th
                  className="p-1 sm:p-2 cursor-pointer"
                  onClick={() =>
                    setSort({
                      column: "apr",
                      order:
                        sort.column === "apr" && sort.order === "asc"
                          ? "desc"
                          : "asc",
                    })
                  }
                >
                  APR %{" "}
                  {sort.column === "apr" && (sort.order === "asc" ? "▲" : "▼")}
                </th>
              )}
              {!isMobile && (
                <th
                  className="p-1 sm:p-2 cursor-pointer"
                  onClick={() =>
                    setSort({
                      column: "30dProfit",
                      order:
                        sort.column === "30dProfit" && sort.order === "asc"
                          ? "desc"
                          : "asc",
                    })
                  }
                >
                  30d %{" "}
                  {sort.column === "30dProfit" &&
                      (sort.order === "asc" ? "▲" : "▼")}
                </th>
              )}
              <th
                className="p-1 sm:p-2 cursor-pointer"
                onClick={() =>
                  setSort({
                    column: "trades",
                    order:
                      sort.column === "trades" && sort.order === "asc"
                        ? "desc"
                        : "asc",
                  })
                }
              >
                Trades{" "}
                {sort.column === "trades" && (sort.order === "asc" ? "▲" : "▼")}
              </th>
              {!isMobile && (
                <th
                  className="p-1 sm:p-2 cursor-pointer"
                  onClick={() =>
                    setSort({
                      column: "totalBtcAmount",
                      order:
                        sort.column === "totalBtcAmount" && sort.order === "asc"
                          ? "desc"
                          : "asc",
                    })
                  }
                >
                  Volume (BTC){" "}
                    {sort.column === "totalBtcAmount" &&
                      (sort.order === "asc" ? "▲" : "▼")}
                </th>
              )}
              <th className="p-1 sm:p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item) => (
              <React.Fragment key={item.address}>
                <tr className="border-t border-gray-600 hover:bg-gray-600 whitespace-nowrap">
                  <td className="p-1 sm:p-2">
                    <i
                      className={`fas fa-chevron-${
                        expandedRows.has(item.address) ? "up" : "down"
                      } text-pink-500 cursor-pointer text-xs sm:text-sm`}
                      onClick={() => toggleExpand(item.address)}
                    />
                  </td>
                  <td className="p-1 sm:p-2 truncate min-w-[100px]">
                    <span
                      className="text-pink-500 cursor-pointer hover:text-pink-400"
                      onClick={() => copyAddress(item.address)}
                    >
                      {item.address.slice(0, 4)}...{item.address.slice(-4)}
                    </span>
                  </td>
                  <td
                    className={`p-1 sm:p-2 ${
                      item.profit >= 0
                        ? "text-green-400"
                        : "text-red-400"
                    }`}
                  >
                    {item.profit}%
                  </td>
                  {!isMobile && (
                    <td
                      className={`p-1 sm:p-2 ${
                        item.apr >= 0 ? "text-green-400" : "text-red-400"
                      }`}
                    >
                      {item.apr}%
                    </td>
                  )}
                  {!isMobile && (
                    <td
                    className={`p-1 sm:p-2 ${
                      item['30dProfit'] !== null
                        ? item['30dProfit'] >= 0
                          ? "text-green-400"
                          : "text-red-400"
                        : ""
                    }`}
                  >
                    {item['30dProfit'] !== null
                      ? `${item['30dProfit']}%`
                        : "N/A"}
                    </td>
                  )}
                  <td className="p-1 sm:p-2">{item.trades}</td>
                  {!isMobile && (
                    <td className="p-1 sm:p-2">{(item.totalBtcAmount / Math.pow(10, 8)).toFixed(5)}</td>
                  )}
                  <td className="p-1 sm:p-2 flex flex-col items-center gap-1 sm:gap-2">
                    <button
                      onClick={() => copyTrade(item.address)}
                      className="bg-pink-600 hover:bg-pink-700 text-white px-2 py-1 rounded text-xs sm:text-sm w-12 sm:w-16"
                      title="Copy to Telegram"
                    >
                      COPY
                    </button>
                    {/* <button
                      onClick={() =>
                        setPopup({
                          type: "chart",
                          data: {
                            address: item.address,
                            profitHistory: item.profitHistory,
                          },
                        })
                      }
                      className="text-pink-500 hover:text-pink-400 text-xs sm:text-sm w-12 sm:w-16 flex justify-center"
                      title="View Chart"
                    >
                      <i className="fas fa-chart-line"></i>
                    </button> */}
                  </td>
                </tr>
                {/* Expanded Trade Details */}
                {expandedRows.has(item.address) && (
                  <tr className="bg-gray-600">
                    <td colSpan="8" className="p-2">
                      <div className="bg-gray-800 p-2 rounded-lg overflow-x-auto">
                        <div className="flex flex-row gap-4 items-center mb-2">
                          <h4 className="text-sm sm:text-base font-semibold">
                            Trade Details
                          </h4>
                          {
                            trades[item.address]?.total > 1 && (
                              <div className="mt-2 flex items-center gap-2 sm:gap-3 mb-[6px]">
                                <button
                                  onClick={() => prevSubPage(item.address)}
                                  disabled={trades[item.address]?.currentPage === 1}
                                  className="bg-pink-600 hover:bg-pink-700 text-white px-[10px] py-1 rounded-lg disabled:opacity-50 text-xs sm:text-sm"
                                >
                                  <i className="fas fa-arrow-left"></i>
                                </button>
                                <span className="text-xs sm:text-sm">{`${trades[item.address]?.currentPage} / ${trades[item.address]?.total}`}</span>
                                <button
                                  onClick={() => nextSubPage(item.address)}
                                  disabled={trades[item.address]?.currentPage === trades[item.address]?.total}
                                  className="bg-pink-600 hover:bg-pink-700 text-white px-[10px] py-1 rounded-lg disabled:opacity-50 text-xs sm:text-sm"
                                >
                                  <i className="fas fa-arrow-right"></i>
                                </button>
                              </div>)
                          }
                        </div>
                        <table className="w-full text-left text-xs sm:text-sm min-w-[640px]">
                          <thead>
                            <tr className="bg-gray-700 sticky top-0 z-10 whitespace-nowrap">
                              <th className="p-1 sm:p-2" title="Rune Ticker">
                                Rune
                              </th>
                              <th
                                className="p-1 sm:p-2"
                                title="Trade Type (Open/Closed)"
                              >
                                Type
                              </th>
                              <th
                                className="p-1 sm:p-2"
                                title="Amount of Runes"
                              >
                                Rune Amt
                              </th>
                              <th
                                className="p-1 sm:p-2"
                                title="Amount in Satoshis"
                              >
                                Amt (sats)
                              </th>
                              <th
                                className="p-1 sm:p-2"
                                title="Open Price per Rune in Satoshis (Unrealized)"
                              >
                                Open (sats)
                              </th>
                              <th
                                className="p-1 sm:p-2"
                                title="Close Price per Rune in Satoshis (Realized)"
                              >
                                Close (sats)
                              </th>
                              <th
                                className="p-1 sm:p-2"
                                title="Market Price per Rune in Satoshis"
                              >
                                Mkt (sats)
                              </th>
                              <th
                                className="p-1 sm:p-2"
                                title="Profit Percentage"
                              >
                                Profit (%)
                              </th>
                              <th
                                className="p-1 sm:p-2"
                                title="Profit in Satoshis"
                              >
                                Profit (sats)
                              </th>
                              <th
                                className="p-1 sm:p-2"
                                title="Copy Trade Status"
                              >
                                Copy
                              </th>
                              <th className="p-1 sm:p-2" title="Trade Date">
                                Date
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {trades[item.address]?.data?.map((trade, idx) => {
                              let profitPercent = 0;
                              let price = Number(trade.btcAmount) / Number(trade.runeAmount)
                              if (trade.mode == "buy") {
                                profitPercent = ((Number(trade.priceFormatted) - price) / price * 100).toFixed(2);
                              } else {
                                profitPercent = ((price - Number(trade.priceFormatted)) / Number(trade.priceFormatted) * 100).toFixed(2);
                              }

                              return (
                                <tr
                                  key={idx}
                                  className={`border-t border-gray-700 ${
                                    idx % 2 === 0
                                      ? "bg-gray-800"
                                      : "bg-gray-700"
                                  }`}
                                >
                                  <td className="p-1 sm:p-2 truncate min-w-[100px]">
                                    <div className="flex items-center gap-1">
                                      <span
                                        className="text-pink-500 cursor-pointer hover:text-pink-400"
                                        onClick={() =>
                                          copyTicker(trade.runeTicker)
                                        }
                                      >
                                        {trade.runeTicker}
                                      </span>
                                      <i
                                        className="fas fa-info-circle text-pink-500 cursor-pointer hover:text-pink-400 text-xs sm:text-sm"
                                        onClick={() =>
                                          handleRuneInfoClick(trade.runeTicker)
                                        }
                                        title="Rune Market Info"
                                      />
                                    </div>
                                  </td>
                                  <td className="p-1 sm:p-2">
                                    {trade.mode === "buy" ? (
                                      <span className="font-bold text-green-400">
                                        Open
                                      </span>
                                    ) : (
                                      <span className="font-bold text-red-400">
                                        Closed
                                      </span>
                                    )}
                                  </td>
                                  <td className="p-1 sm:p-2">
                                    {Number(trade.runeAmount).toFixed(2)}
                                  </td>
                                  <td className="p-1 sm:p-2">
                                    {Number(trade.btcAmount).toFixed(0)}
                                  </td>
                                  <td className="p-1 sm:p-2">
                                    {trade.mode === "sell"
                                      ? Number(trade.priceFormatted).toFixed(3)
                                      : (Number(trade.btcAmount) / Number(trade.runeAmount)).toFixed(3)}
                                  </td>
                                  <td className="p-1 sm:p-2">
                                    {trade.mode === "buy"
                                      ? "-"
                                      : (Number(trade.btcAmount) / Number(trade.runeAmount)).toFixed(3)}
                                  </td>
                                  <td className="p-1 sm:p-2">
                                    {Number(trade.priceFormatted).toFixed(3)}
                                  </td>
                                  <td className={`p-1 sm:p-2 ${profitPercent >= 0 ? "text-green-400" : "text-red-400"}`}>
                                    {profitPercent}
                                  </td>
                                  <td className={`p-1 sm:p-2 ${profitPercent >= 0 ? "text-green-400" : "text-red-400"}`}>
                                    {(profitPercent * Number(trade.btcAmount) / 100).toFixed(0)}
                                  </td>
                                  <td className="p-1 sm:p-2">
                                    {trade.copyTrade === 1 ? "Yes" : "No"}
                                  </td>
                                  <td className="p-1 sm:p-2 truncate min-w-[120px]">
                                    {new Date(trade.createdAt).toLocaleString()}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>

      {/* Pagination Controls */}
      {
        totalPages > 1 && (
          <div className="mt-2 flex justify-center items-center gap-2 sm:gap-4 w-full">
            <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="bg-pink-600 hover:bg-pink-700 text-white px-[10px] py-1 rounded-lg disabled:opacity-50 text-xs sm:text-sm"
          >
            <i className="fas fa-arrow-left"></i>
          </button>
          <span className="text-xs sm:text-sm">{`${currentPage} / ${totalPages}`}</span>
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            className="bg-pink-600 hover:bg-pink-700 text-white px-[10px] py-1 rounded-lg disabled:opacity-50 text-xs sm:text-sm"
          >
            <i className="fas fa-arrow-right"></i>
          </button>
        </div>)
      }
    </div>  
  );
};

export default TradingTable;
